import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { ContentBoxTitle } from "../../../components/common";

import { Info } from "@mui/icons-material";
import { FormButtonGroup, FormStack } from "../../../components/form";
import { oids, OrganizationPartOf } from "../../../configs/Settings";
import { useSessionContext } from "../../../contexts/SessionContext";
import {
  PutOrganization,
  PutPractitioner,
  PutPractitionerRole,
} from "../../../services/fhir";
import dayjs from "dayjs";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { handleKeyPress } from "../../../utils/useKeyPress";
import TermText from "../../TermsOfUse/components/TermText";
import { IFormInput } from "../types";

interface IProps {
  handleSubmit: any;
  onBackButtonClick: Function;
  onNextButtonClick: Function;
}

export function TermsOfUse({
  handleSubmit,
  onBackButtonClick,
  onNextButtonClick,
}: IProps) {
  const [checkbox, setCheckbox] = useState(false);
  const { user } = useSessionContext();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: IFormInput) => {
    // Verifica se o checkbox está marcado
    if (!checkbox) {
      toast.error(
        "A aceitação dos Termos de Uso e Política de Privacidade é obrigatória para continuar."
      );
      return; // Interrompe a execução da função aqui
    }

    data.organization.id =
      `${oids.cnpj}-${data.organization?.identifier[0]?.value}` || "";

    const organizationId = data.organization.id;

    setLoading(true);
    try {
      const auxOrganization =
        OrganizationPartOf?.id?.length &&
        data.organization?.identifier[0]?.value !== OrganizationPartOf?.id
          ? {
              ...data?.organization,
              partOf: {
                reference: `Organization/${oids.cnpj}-${OrganizationPartOf?.id}`,
                display: `${OrganizationPartOf?.display}`,
              },
            }
          : data?.organization;
      const organizationResponse = await PutOrganization(auxOrganization);
      const auxDescription = organizationResponse?.parameter?.find(
        (el: any) => el.name === "description"
      )?.valueString;
      const { id: practitionerId } = await PutPractitioner({
        name: user.name,
        identifier: user.username,
        birthDate: user.birthDate,
      });
      await PutPractitionerRole({
        organizationId,
        organizationDisplay: auxDescription,
        practitionerId,
        practitionerDisplay: user.name,
      });

      // Contrato de acesso gratuito (organização)
      // - `externalReference = "3"`: quantidade de profissionais permitidos no plano gratuito
      // - `term[0].identifier.value = "3"`: limite efetivo aplicado nas telas de profissionais
      // - Sem `nextPaymentDueDate`: contratos gratuitos não têm próxima cobrança
      const freePlanContract = {
        resourceType: "Contract",
        extension: [
          {
            url: "https://fluxmed.com.br/hapi/fhir/R5/extensions/subscriptionType",
            valueString: "Organização - Acesso gratuito",
          },
          {
            url: "https://fluxmed.com.br/hapi/fhir/R5/extensions/externalReference",
            valueString: "3",
          },
        ],
        identifier: [
          {
            system: "https://fluxmed.com.br/hapi/fhir/R5/contract-ids",
            value: "1620149873",
          },
          {
            system: "https://fluxmed.com.br/hapi/fhir/R5/contract-type",
            value: "subscription",
          },
        ],
        status: "executed",
        contentDerivative: {
          coding: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-origins",
              code: "fluxmed",
            },
          ],
        },
        authority: [
          {
            reference: `Organization/${organizationId}`,
          },
        ],
        type: {
          coding: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
              code: "subscription",
            },
          ],
        },
        term: [
          {
            identifier: {
              system: "https://fluxmed.com.br/hapi/fhir/R5/maxPraticioners",
              value: "3",
            },
            issued: dayjs().toISOString(),
            applies: {
              start: dayjs().toISOString(),
            },
            text: "Organização - Plano gratuito",
            offer: {
              type: {
                coding: [
                  {
                    system:
                      "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                    code: "subscription",
                  },
                ],
              },
            },
            asset: [
              {
                valuedItem: [
                  {
                    entityCodeableConcept: {
                      coding: [
                        {
                          system:
                            "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                          code: "subscription-fee",
                        },
                      ],
                    },
                    quantity: {
                      value: 1,
                      unit: "subscription",
                    },
                    unitPrice: {
                      value: 0,
                      currency: "BRL",
                    },
                  },
                ],
              },
            ],
          },
        ],
      };

      await PostContract({ data: freePlanContract });
      toast.success("Organização criada com sucesso!");
      onNextButtonClick(organizationId);
    } catch (err) {
      toast.error("Não foi possível criar a organização");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onKeyDown={(event) =>
        handleKeyPress(event, loading, handleSubmit(onSubmit))
      }
    >
      <Stack width="100%" mt={2} spacing={2}>
        <Stack width="100%" justifyContent="center" alignItems="center">
          <ContentBoxTitle>
            Termos de Uso e Política de Privacidade
          </ContentBoxTitle>
        </Stack>
        <Box
          maxHeight="492px"
          sx={{
            width: "fit-content",
            overflowY: "scroll",
            mt: 2,
            mb: 2,
          }}
        >
          <Box height="40vh">
            <TermText termOf="owner" />
          </Box>
        </Box>
        <FormGroup sx={{ width: "fit-content", textAlign: "left" }}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                onChange={() => setCheckbox(!checkbox)}
              />
            }
            label="Eu li e estou de acordo com os Termos de Uso e Politica de Privacidade."
          />
        </FormGroup>
        {!checkbox && (
          <Stack
            direction="row"
            justifyContent="left"
            alignItems="center"
            spacing={2}
          >
            <Info sx={{ color: "neutral300.main" }} />
            <Typography
              variant="body1"
              fontWeight={400}
              color="neutral600.main"
              sx={{ maxWidth: "900px" }}
            >
              Aceite os Termos de Uso e Política de Privacidade para continuar o
              acesso a plataforma e seus serviços, ao recusar os Termos de Uso e
              Política de Privacidade,pode levar a perda de acesso a plataforma
              e seus serviços.
            </Typography>
          </Stack>
        )}
        <Stack
          direction="row-reverse"
          width="100%"
          mt={3}
          spacing={3}
          justifyContent="space-between"
        >
          <FormButtonGroup
            maxWidth
            onGoBackButtonClick={() => onBackButtonClick()}
            onNextButtonClick={handleSubmit(onSubmit)}
            goBackButtonText="Recusar"
            nextButtonText="Aceitar"
            loading={loading}
            disabled={checkbox === false}
          />
        </Stack>
      </Stack>
    </form>
  );
}
